/**
 * Sanitizes user input to prevent XSS attacks
 * Removes dangerous HTML tags, scripts, and event handlers
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';
  
  return input
    .trim()
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove iframe tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    // Remove on* event handlers (onclick, onload, etc.)
    .replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:text\/html/gi, '');
  // Note: We intentionally do NOT escape <, >, ", ', / as HTML entities
  // because that breaks URLs. For HTML rendering, use proper escaping at render time.
}

/**
 * Sanitizes input for display in HTML context (escapes special chars)
 */
export function sanitizeForHtml(input: string): string {
  if (!input) return '';
  
  return sanitizeInput(input)
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * Sanitizes an object's string values
 */
export function sanitizeObject<T extends Record<string, unknown>>(obj: T): T {
  const sanitized = { ...obj } as Record<string, unknown>;
  
  for (const key in sanitized) {
    const val = sanitized[key];
    if (typeof val === 'string') {
      sanitized[key] = sanitizeInput(val);
    } else if (Array.isArray(val)) {
      sanitized[key] = val.map((item) => (typeof item === 'string' ? sanitizeInput(item) : item));
    }
  }
  
  return sanitized as T;
}

/**
 * Validates and sanitizes URL to prevent XSS through href attributes
 */
export function sanitizeUrl(url: string): string {
  if (!url) return '';
  
  const trimmedUrl = url.trim().toLowerCase();
  
  // Block dangerous protocols
  if (
    trimmedUrl.startsWith('javascript:') ||
    trimmedUrl.startsWith('data:') ||
    trimmedUrl.startsWith('vbscript:') ||
    trimmedUrl.startsWith('file:')
  ) {
    return '';
  }
  
  return url.trim();
}

/**
 * Truncates string to max length safely
 */
export function truncate(str: string, maxLength: number): string {
  if (!str) return '';
  return str.length > maxLength ? str.substring(0, maxLength) : str;
}
